/**
 * Events pushed from main/worker to the renderer through the preload bridge. Account-scoped
 * events always carry the accountId they started with (A02).
 */
import { z } from 'zod';
import { AccountIdSchema, ArtifactIdSchema, ConnectionIdSchema, DatasetIdSchema, DeviceIdSchema, NotificationIdSchema, OperationIdSchema, OutreachBatchIdSchema, ProviderSchema, RumiTurnIdSchema, RunIdSchema, SnapshotIdSchema } from '../ids.js';
import { EntitlementStateSchema, SyncErrorCodeSchema, SyncJobStateSchema } from '../status.js';
import { NotificationActionSchema, NotificationCategorySchema, NotificationLevelSchema, NotificationSourceSchema, SessionUserSchema } from '../api/entities.js';
import { IsoDateTimeSchema, SemVerSchema } from '../api/common.js';
import { ArtifactSchema, OutreachItemStateSchema, RumiTurnSchema, SyncIssueSchema } from '../providers/records.js';
import { AppPhaseSchema, DatasetProgressSchema, GoogleStatusSchema, LOCK_REASONS, LockReasonSchema, NotificationDedupeKeySchema, NotificationsChangedReasonSchema, RecoveryStateSchema, UpdateCheckResultSchema } from './shapes.js';

const count = z.number().int().nonnegative();

/** Only stable lifecycle/retry enums travel to analytics; never arbitrary lock messages. */
export const SyncPauseReasonSchema = z.enum([...LOCK_REASONS, 'app_lock', 'sign_out', 'shutdown', 'update', 'checkpoint', 'retry_budget_exhausted', 'provider_retry_after']);
export type SyncPauseReason = z.infer<typeof SyncPauseReasonSchema>;

// added by desktop-main: menu/keyboard shortcuts delivered to the renderer (spec section 22).
// The renderer decides whether a shortcut applies (text-editor focus is respected there).
export const SHORTCUT_IDS = [
  'palette',
  'view.overview',
  'view.sources',
  'view.agendas',
  'view.outreach',
  'view.rumi',
  'toggleRail',
  'toggleJobs',
  'toggleInspector',
  'findInView',
  'settings',
  'updates',
  'documentation',
  'newAgenda',
  'syncNow',
  'notepad',
  'notifications', // notifications change: ⌘⇧N opens the notification center
  // single-view change (2026-10-23): the workspace tab ids (tab.next/prev/close/moveLeft/moveRight)
  // are gone with the tab strip; one view is open at a time and ⌘W closes the window again.
  // appearance change: interface size presets stepped from the View menu (⌘= / ⌘− / ⌘0).
  'zoom.in',
  'zoom.out',
  'zoom.reset',
] as const;
export const ShortcutIdSchema = z.enum(SHORTCUT_IDS);
export type ShortcutId = z.infer<typeof ShortcutIdSchema>;

export const EVENTS = {
  'system.updateChanged': UpdateCheckResultSchema,
  'sync.progress': z.object({
    accountId: AccountIdSchema,
    runId: RunIdSchema,
    connectionId: ConnectionIdSchema,
    state: SyncJobStateSchema,
    datasetProgress: z.array(DatasetProgressSchema),
    waitingUntil: IsoDateTimeSchema.nullable().optional(),
    /** e.g. "Waiting for Vanta · retry in 18s" */
    message: z.string(),
    percent: z.number().min(0).max(100).nullable(),
    requestCount: count,
    at: IsoDateTimeSchema,
  }),
  'sync.stateChanged': z.object({
    accountId: AccountIdSchema,
    runId: RunIdSchema,
    connectionId: ConnectionIdSchema,
    from: SyncJobStateSchema.nullable(),
    to: SyncJobStateSchema,
    at: IsoDateTimeSchema,
    statusText: z.string(),
    snapshotId: SnapshotIdSchema.nullable(),
    resumeRequired: z.boolean(),
    // GET-103: run facts for product analytics (sync_finished / sync_failed). Counts and codes only;
    // optional so payloads from older workers still parse.
    provider: ProviderSchema.optional(),
    /** Time since the run started (queued when it never started), in milliseconds. */
    elapsedMs: count.optional(),
    /** Provider requests made so far (the run summary as last persisted). */
    requestCount: count.optional(),
    retryCount: count.optional(),
    rateLimitWaits: count.optional(),
    /** Total time spent waiting on retries and provider rate limits, in milliseconds. */
    retryWaitMs: count.optional(),
    /** Stable failure/retry code; lifecycle pauses have a separate reason and a null error. */
    errorCode: SyncErrorCodeSchema.nullable().optional(),
    pauseReason: SyncPauseReasonSchema.nullable().optional(),
    datasetId: DatasetIdSchema.nullable().optional(),
    datasetRequests: z.partialRecord(DatasetIdSchema, count).optional(),
  }),
  // GET-103: a run waited for the provider. Emitted by the worker at most once per run every
  // ~30 seconds (jobs/rateLimitWaitThrottle.ts) so analytics can tell pacing from 429 back-off
  // without changing the sync itself; the renderer ignores it.
  'sync.rateLimitWait': z.object({
    accountId: AccountIdSchema,
    runId: RunIdSchema,
    connectionId: ConnectionIdSchema,
    provider: ProviderSchema,
    /** `pacing`: the local limiter held the request below the published ceiling; `provider_429`: the provider answered 429. */
    reason: z.enum(['pacing', 'provider_429']),
    waitMs: count,
    at: IsoDateTimeSchema,
  }),
  'entitlement.changed': z.object({
    state: EntitlementStateSchema,
    expiresAt: IsoDateTimeSchema.nullable(),
    remainingSeconds: count.nullable(),
    policyVersion: z.number().int().positive().nullable(),
    reason: z.string().nullable(),
  }),
  'session.changed': z.object({
    phase: AppPhaseSchema,
    user: SessionUserSchema.nullable(),
    deviceId: DeviceIdSchema.nullable(),
    online: z.boolean(),
  }),
  'content.updated': z.object({
    version: SemVerSchema,
    previousVersion: SemVerSchema.nullable(),
    result: z.enum(['downloaded', 'activated', 'rejected_signature', 'rejected_schema', 'rejected_compatibility']),
    itemCount: count,
    changedItemCount: count,
    reason: z.string().nullable(),
  }),
  'rumi.delta': z.object({
    accountId: AccountIdSchema,
    operationId: OperationIdSchema,
    turnId: RumiTurnIdSchema,
    text: z.string(),
  }),
  'rumi.toolCall': z.object({
    accountId: AccountIdSchema,
    operationId: OperationIdSchema,
    turnId: RumiTurnIdSchema,
    tool: z.string(),
    summary: z.string(),
    /** Batch progress for Full Review. */
    processed: count.nullable(),
    total: count.nullable(),
  }),
  'rumi.completed': z.object({
    accountId: AccountIdSchema,
    operationId: OperationIdSchema,
    turn: RumiTurnSchema,
  }),
  'rumi.failed': z.object({
    accountId: AccountIdSchema,
    operationId: OperationIdSchema,
    turnId: RumiTurnIdSchema,
    error: z.object({ code: z.string(), message: z.string() }),
    /** False when a cancelled/failed stream may have incurred unreported charges. */
    usageKnown: z.boolean(),
    partialTurn: RumiTurnSchema.nullable(),
  }),
  notification: z.object({
    id: z.string(),
    level: NotificationLevelSchema,
    category: NotificationCategorySchema,
    title: z.string(),
    body: z.string(),
    actions: z.array(NotificationActionSchema),
    accountId: AccountIdSchema.nullable(),
    required: z.boolean(),
    createdAt: IsoDateTimeSchema,
    // notifications change: who created it. Defaults keep payloads from older emitters parseable.
    source: NotificationSourceSchema.default('desktop'),
    dedupeKey: NotificationDedupeKeySchema.nullable().default(null),
  }),
  // notifications change: counts changed (bell, dock badge, renderer refetch). Emitted after the
  // worker/main persisted the change.
  'notifications.changed': z.object({
    unreadCount: count,
    requiredUnread: count,
    reason: NotificationsChangedReasonSchema,
  }),
  // notifications change: a native macOS notification was clicked; main focused the window and
  // marked it read, the renderer runs the first action.
  'notification.activate': z.object({ id: NotificationIdSchema }),
  'audit.outboxChanged': z.object({
    pending: count,
    lastAckSequence: count.nullable(),
    lastDeliveryAt: IsoDateTimeSchema.nullable(),
    lagSeconds: count.nullable(),
    faulted: z.boolean(),
  }),
  'google.changed': GoogleStatusSchema,
  'storage.warning': z.object({
    usedBytes: count,
    budgetBytes: count,
    ratio: z.number(),
    freeDiskBytes: count.nullable(),
    paused: z.boolean(),
  }),
  lock: z.object({
    reason: LockReasonSchema,
    at: IsoDateTimeSchema,
    remainingSeconds: count.nullable(),
    /** Autosave/checkpoint completed before locking. */
    checkpointed: z.boolean(),
  }),
  unlock: z.object({ at: IsoDateTimeSchema }),
  'jobs.issue': z.object({
    accountId: AccountIdSchema,
    runId: RunIdSchema,
    issue: SyncIssueSchema,
  }),
  'outreach.batchChanged': z.object({
    accountId: AccountIdSchema,
    batchId: OutreachBatchIdSchema,
    stateCounts: z.record(OutreachItemStateSchema, count),
    at: IsoDateTimeSchema,
  }),
  'artifact.changed': z.object({
    accountId: AccountIdSchema,
    artifactId: ArtifactIdSchema,
    artifact: ArtifactSchema,
  }),
  'worker.status': z.object({
    // 'recovery' added by desktop-main fix pass (LH-03/F05): the worker is running but its profile
    // database refused to open; commands fail with recovery_required until resolved.
    state: z.enum(['starting', 'ready', 'restarting', 'failed', 'recovery']),
    restartCount: count,
    message: z.string().nullable(),
  }),
  // added by desktop-main fix pass (LH-03/F05): emitted by the worker when the profile database
  // needs recovery (A21); main mirrors it into the app phase and the renderer shows the screen.
  'worker.recovery': RecoveryStateSchema,
  'accounts.changed': z.object({
    accountIds: z.array(AccountIdSchema),
    // local-accounts change: `created` (local insert), `registry_report_delivered` (outbox flushed).
    reason: z.enum(['entitlement', 'registered', 'created', 'renamed', 'archived', 'deleted_local', 'prefs', 'snapshot', 'connection', 'registry_report_delivered']),
  }),
  'sources.descriptionsChanged': z.object({ accountId: AccountIdSchema }),
  // added by desktop-main: application menu shortcut pressed (see SHORTCUT_IDS).
  shortcut: z.object({ id: ShortcutIdSchema }),
  // added by desktop-worker-workflows: worker -> main broker requests (spec section 14). Main
  // consumes these (CredentialGrantBroker, GoogleAuth); they carry ids and amounts only.
  'rumi.grantNeeded': z.object({
    operationId: OperationIdSchema,
    accountId: AccountIdSchema,
    budgetReservationMicroUsd: count,
    model: z.string().max(128).optional(),
  }),
  'rumi.grantReleased': z.object({ operationId: OperationIdSchema, reason: z.string().max(64).optional() }),
  'google.tokenNeeded': z.object({ reason: z.enum(['missing', 'expired', 'unauthorized']), at: IsoDateTimeSchema }),
  'google.unauthorized': z.object({ at: IsoDateTimeSchema, httpStatus: count.nullable() }),
} as const;

export type Events = typeof EVENTS;
export type EventName = keyof Events;
export type EventPayload<E extends EventName> = z.infer<Events[E]>;

export const EVENT_NAMES = Object.keys(EVENTS) as EventName[];
export const EventNameSchema = z.enum(EVENT_NAMES as [EventName, ...EventName[]]);

/** Wire envelope from main to renderer. */
export const EventEnvelopeSchema = z.object({
  event: EventNameSchema,
  payload: z.unknown(),
  at: IsoDateTimeSchema,
  seq: count,
});
export type EventEnvelope = z.infer<typeof EventEnvelopeSchema>;

export function parseEventPayload<E extends EventName>(event: E, payload: unknown): EventPayload<E> {
  return EVENTS[event].parse(payload) as EventPayload<E>;
}

export function isEventName(value: unknown): value is EventName {
  return typeof value === 'string' && Object.hasOwn(EVENTS, value);
}
